import React, { useState, useCallback, useEffect } from 'react';
import { useTransactionAnimation } from '../../contexts/TransactionAnimationContext';
import { useGameSession } from '../../contexts/GameSessionProvider';
// import { useProfileSetup } from '../../contexts/ProfileSetupContext';
// Remove old context imports
// import { useGameMode } from '../../contexts/GameModeContext';
// import { useGame } from '../../contexts/GameContext';
import { useTransactions } from '../../hooks/useTransactions';
import { solanaUtils } from '../../utils/solanaUtils';
import { validateWalletAddress } from '../../utils/withdrawUtils';
import { useWallet } from '../../contexts/WalletProvider';
import { useVoucherContext } from '../../contexts/VoucherContext';
import { useReferral } from '../../contexts/ReferralContext';

import './Wallet.css';

// Constants
const NOTIFICATION_DURATION = 2000;

// Types
interface WalletProps {
  className?: string;
  onLogout?: () => void;
  onPastGamesClick?: () => void;
}

// Utility Functions
const formatters = {
  balance: (value: number): string => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toFixed(2)}`;
  },

  solBalance: (value: number): string => {
    return `${value.toFixed(4)} SOL`;
  },

  solBalancePrecise: (value: number): string => {
    return `${value.toFixed(6)} SOL`;
  },

  profit: (value: number): string => {
    return `${value >= 0 ? '+' : ''}$${Math.abs(value).toFixed(2)}`;
  },

  address: (address: string): string => {
    return `${address.slice(0, 8)}...${address.slice(-8)}`;
  },

  // Unified transaction amount formatting for both modes
  transactionAmount: (amount: number, isGameMode: boolean): string => {
    if (isGameMode) {
      // Game Mode: Use dollar format
      return `${amount > 0 ? '+' : ''}$${Math.abs(amount).toFixed(2)}`;
    } else {
      // Live Mode: Use SOL format (compact, no "SOL" suffix)
      const sign = amount > 0 ? '+' : '';
      return `${sign}${Math.abs(amount).toFixed(4)}`;
    }
  }
};

const userUtils = {
  getDisplayName: (user: any): string => {
    const twitterAccount = user?.linkedAccounts?.find((account: any) => account.type === 'twitter_oauth');
    if (twitterAccount?.name) {
      return twitterAccount.name;
    }
    if (twitterAccount?.username) {
      return `@${twitterAccount.username}`;
    }
    return user?.id?.slice(-8) || 'User';
  }
};

// Custom Hooks

const useDropdownControl = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.wallet')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return { isDropdownOpen, setIsDropdownOpen };
};

const useClipboard = () => {
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), NOTIFICATION_DURATION);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  };

  return { showCopyNotification, copyToClipboard };
};

// Sub-components  
const LoadingState: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`wallet loading ${className || ''}`}>
    <div className="wallet-summary">
      <div className="wallet-balance">
        <div className="loading-spinner"></div>
      </div>
    </div>
  </div>
);

const LoginButton: React.FC<{ onLogin: () => void }> = ({ onLogin }) => (
  <button onClick={onLogin} className="login-button">
     CONNECT
  </button>
);

const TransactionAnimation: React.FC<{ 
  isAnimating: boolean;
  transactionAmount: number | null;
  isGameMode: boolean;
}> = ({ isAnimating, transactionAmount, isGameMode }) => {
  if (!isAnimating || transactionAmount === null) return null;
  
  return (
    <div className={`transaction-amount ${transactionAmount >= 0 ? 'positive' : 'negative'}`}>
      {formatters.transactionAmount(transactionAmount, isGameMode)}
    </div>
  );
};

const WalletSummary: React.FC<{
  solBalance: number | null;
  isLoadingBalance: boolean;
}> = ({ solBalance, isLoadingBalance }) => {
  
  return (
    <div className="wallet-summary">
      <img 
        src="/assets/icons/solanaLogoMark.svg" 
        alt="SOL" 
        className="wallet-sol-icon"
      />
      <div className="wallet-balance">
        {isLoadingBalance ? (
          <span className="balance-loading">...</span>
        ) : (
          <div className="balance-display">
            {false ? (
              // Show voucher credits with promo styling
              <span className="balance-amount voucher-active">
                {formatters.solBalance(0)}
              </span>
            ) : (
              // Show regular SOL balance (number only, no "SOL" suffix)
              <span className="balance-amount">
                {solBalance !== null ? solBalance.toFixed(4) : '--'}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Remove ModeToggle component since we're live mode only
/*
const ModeToggle: React.FC<{
  isGameMode: boolean;
  gameInProgress: boolean;
  onModeChange: (mode: 'game' | 'live') => void;
}> = ({ isGameMode, gameInProgress, onModeChange }) => (
  <div className="dropdown-mode-toggle">
    <div className="mode-toggle">
      <button 
        className={`mode-button ${isGameMode ? 'active' : ''}`}
        onClick={() => onModeChange('game')}
        disabled={gameInProgress}
        title={gameInProgress ? 'Complete current session to switch modes' : 'Switch to Game Mode'}
      >
        🎮 Game
      </button>
      <button 
        className={`mode-button ${!isGameMode ? 'active' : ''}`}
        onClick={() => onModeChange('live')}
        disabled={gameInProgress}
        title={gameInProgress ? 'Complete current session to switch modes' : 'Switch to Live Mode'}
      >
        💰 Live
      </button>
    </div>
  </div>
);
*/

const WalletDropdown: React.FC<{
  user: any;
  // Remove isGameMode and balance since we're live mode only
  // isGameMode: boolean;
  // balance: number;
  solBalance: number | null;
  isLoadingBalance: boolean;
  solanaWallet: any;
  showCopyNotification: boolean;
  // Remove gameInProgress since there's no game concept
  // gameInProgress: boolean;
  showWithdrawForm: boolean;
  withdrawAddress: string;
  withdrawAmount: string;
  isWithdrawing: boolean;
  promoCode: string;
  
  // Voucher-related props
  hasVoucherCredits: boolean;
  totalVoucherCredits: number;
  showPromoForm: boolean;
  isClaimingPromo: boolean;
  
  // Referral-related props
  showReferralSection: boolean;
  myReferralCode: string | null;
  referralStats: any;
  referralEarnings: any;
  referralLoading: any;
  showCopyReferralNotification: boolean;

  // Remove onModeChange since no mode switching
  // onModeChange: (mode: 'game' | 'live') => void;
  onCopyAddress: () => void;
  onLogout: () => void;
  onClose: () => void;
  onWithdrawClick: () => void;
  onWithdrawCancel: () => void;
  onWithdrawSubmit: () => void;
  onWithdrawAddressChange: (value: string) => void;
  onWithdrawAmountChange: (value: string) => void;

  onPromoCodeChange: (value: string) => void;
  onPromoClick: () => void;
  onPromoCancel: () => void;
  onPromoSubmit: () => void;
  onManualRefresh?: () => void;
  getMaxWithdrawable?: () => number;
  onProfileClick: () => void;
  
  onReferralClick: () => void;
  onReferralCancel: () => void;
  onCopyReferralLink: () => void;
  onClaimEarnings: () => void;
  onPastGamesClick: () => void;

}> = ({ 
  user, 
  // isGameMode, 
  // balance, 
  solBalance, 
  isLoadingBalance, 
  solanaWallet, 
  showCopyNotification,
  // gameInProgress,
  showWithdrawForm,
  withdrawAddress,
  withdrawAmount,
  isWithdrawing,
  promoCode,

  // Voucher props
  hasVoucherCredits,
  totalVoucherCredits,
  showPromoForm,
  isClaimingPromo,
  
  // Referral props
  showReferralSection,
  myReferralCode,
  referralStats,
  referralEarnings,
  referralLoading,
  showCopyReferralNotification,

  // onModeChange,
  onCopyAddress,
  onLogout,
  onClose,
  onWithdrawClick,
  onWithdrawCancel,
  onWithdrawSubmit,
  onWithdrawAddressChange,
  onWithdrawAmountChange,

  onPromoCodeChange,
  onPromoClick,
  onPromoCancel,
  onPromoSubmit,
  onManualRefresh,
  getMaxWithdrawable,
  onProfileClick,
  
  onReferralClick,
  onReferralCancel,
  onCopyReferralLink,
  onClaimEarnings,
  onPastGamesClick
}) => (
  <div className="wallet-dropdown">
    {/* Game Mode Toggle Removed - Live Mode Only */}
    {/* <ModeToggle
      isGameMode={isGameMode}
      gameInProgress={gameInProgress}
      onModeChange={onModeChange}
    /> */}

    <div className="dropdown-header">
      <div className="user-details">
        <span className="full-username">{userUtils.getDisplayName(user)}</span>
        <div className="balance-detail">
          {/* isGameMode ? (
            <>
              <span className="balance-amount-large">
                {formatters.balance(balance)}
              </span>
              <span className="network-indicator">Game Mode</span>
            </>
          ) : ( */}
            <>
              {/* Adaptive display: Show voucher credits if available, otherwise show regular SOL */}
              {hasVoucherCredits ? (
                <span className="balance-amount-large voucher-active">
                  {isLoadingBalance ? 'Loading...' : `${formatters.solBalancePrecise(totalVoucherCredits)} CREDITS`}
                </span>
              ) : (
                <span className="balance-amount-large">
                  {isLoadingBalance ? 'Loading...' : formatters.solBalancePrecise(solBalance || 0)}
                </span>
              )}
              <span className="network-indicator">
                {solanaUtils.getNetworkName()} • Live Mode {hasVoucherCredits ? '• CREDITS ACTIVE' : ''}
              </span>
            </>
          {/* ) */}
        </div>
      </div>
    </div>
    
    {solanaWallet && (
      <div className="wallet-details">
        <div className="wallet-address-section">
          <span className="address-label">Wallet Address:</span>
          <span 
            className="address-full" 
            title={`Click to copy: ${solanaWallet.address}`}
            onClick={onCopyAddress}
          >
            {formatters.address(solanaWallet.address)}
          </span>
        </div>
        
        {showCopyNotification && (
          <div className="copy-notification">
            ✅ Address copied to clipboard!
          </div>
        )}
      </div>
    )}

    <div className="dropdown-actions">
      {/* Always show withdraw button since we're live mode only */}
      {solBalance !== null && solBalance > 0.001 && !showWithdrawForm && (
        <button 
          onClick={onWithdrawClick}
          className="withdraw-button"
        >
          💸 Withdraw SOL
        </button>
      )}
      
      {showWithdrawForm && (
        <div className="withdraw-form">
          <div className="withdraw-form-header">
            <span>Withdraw SOL</span>
            <button onClick={onWithdrawCancel} className="withdraw-cancel">✕</button>
          </div>
          
          <div className="withdraw-form-field">
            <label>To Address:</label>
            <input
              type="text"
              value={withdrawAddress}
              onChange={(e) => onWithdrawAddressChange(e.target.value)}
              placeholder="Enter wallet address..."
              className="withdraw-input"
            />
          </div>
          
          <div className="withdraw-form-field">
            <label>Amount (SOL):</label>
            <div className="withdraw-amount-field">
              <input
                type="number"
                value={withdrawAmount}
                onChange={(e) => onWithdrawAmountChange(e.target.value)}
                placeholder="0.000"
                step="0.001"
                min="0"
                max={solBalance?.toString()}
                className="withdraw-input"
              />
              <button 
                onClick={() => onWithdrawAmountChange(getMaxWithdrawable?.()?.toString() || '0')}
                className="withdraw-max-button"
              >
                MAX
              </button>
            </div>
            <span className="withdraw-max-label">Max: {getMaxWithdrawable?.()?.toFixed(4) || '0.0000'} SOL</span>
          </div>
          
          <div className="withdraw-form-actions">
            <button 
              onClick={onWithdrawSubmit}
              disabled={isWithdrawing || !withdrawAddress || !withdrawAmount}
              className="withdraw-submit"
            >
              {isWithdrawing ? 'Withdrawing...' : 'Withdraw'}
            </button>
          </div>
        </div>
      )}
      
      {/* Referral button - always show when no forms are open */}
      {!showWithdrawForm && !showReferralSection && !showPromoForm && (
        <button 
          onClick={onReferralClick}
          className="referral-button"
        >
          🎁 Referrals
        </button>
      )}
      
      {/* Past Games button - always show when no forms are open */}
      {!showWithdrawForm && !showReferralSection && !showPromoForm && (
        <button 
          onClick={onPastGamesClick}
          className="past-games-button"
        >
          📊 Past Games
        </button>
      )}
      
      {/* Referral section */}
      {showReferralSection && (
        <div className="referral-section">
          <div className="referral-section-header">
            <span>Invite & Earn</span>
            <button onClick={onReferralCancel} className="referral-cancel">✕</button>
          </div>
          
          {myReferralCode ? (
            <>
              <p className="referral-subtitle">
                Bring friends to FEEN & Earn 25% of profits from every player you invite!
              </p>
              
              {referralStats && referralEarnings && (
                <div className="referral-stats-row">
                  <div className="referral-stat">
                    <span className="stat-value">{referralStats.totalReferrals || 0}</span>
                    <span className="stat-label">Referrals</span>
                  </div>
                  <div className="referral-stat-divider"></div>
                  <div className="referral-stat">
                    <span className="stat-value">
                      <img 
                        src="/assets/icons/solanaLogoMark.svg" 
                        alt="SOL" 
                        className="stat-sol-icon"
                      />
                      {referralEarnings.totalEarned.toFixed(2)}
                    </span>
                    <span className="stat-label">Total Earned</span>
                  </div>
                </div>
              )}
              
              <div className="referral-link-section">
                <label className="referral-link-label">Your Referral Link</label>
                <div className="referral-link-container">
                  <input
                    type="text"
                    value={`${window.location.origin}/${myReferralCode}`}
                    readOnly
                    className="referral-link-input"
                    onClick={(e) => e.currentTarget.select()}
                    ref={(input) => {
                      if (input) {
                        input.scrollLeft = input.scrollWidth;
                      }
                    }}
                  />
                  <button 
                    onClick={onCopyReferralLink}
                    className="referral-copy-btn"
                    title="Copy link"
                  >
                    {showCopyReferralNotification ? '✓' : '📋'}
                  </button>
                </div>
              </div>
              
              {/* Earnings Section */}
              {referralEarnings && (
                <div className="referral-earnings-section">
                  <div className="earnings-row">
                    <div className="earnings-display-inline">
                      <div className="earnings-amount">
                        <img 
                          src="/assets/icons/solanaLogoMark.svg" 
                          alt="SOL" 
                          className="earnings-sol-icon"
                        />
                        {referralEarnings.claimableNow.toFixed(2)}
                      </div>
                      <button 
                        onClick={onClaimEarnings}
                        className={`claim-earnings-btn ${referralLoading.claiming ? 'claiming' : ''}`}
                        disabled={referralLoading.claiming || !referralEarnings.canClaimNow || referralEarnings.claimableNow <= 0}
                      >
                        {referralLoading.claiming ? (
                          <>
                            <span className="loading-dots">
                              <span></span>
                              <span></span>
                              <span></span>
                            </span>
                          </>
                        ) : (
                          'Claim'
                        )}
                      </button>
                    </div>
                    <span className="earnings-subtitle">
                      {!referralEarnings.canClaimNow && referralEarnings.nextClaimAvailable 
                        ? (() => {
                            const now = new Date().getTime();
                            const next = new Date(referralEarnings.nextClaimAvailable).getTime();
                            const hoursLeft = Math.ceil((next - now) / (1000 * 60 * 60));
                            return `Next claim in ${hoursLeft}h`;
                          })()
                        : 'Claimable'}
                    </span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="referral-loading">
              <div className="spinner" />
              <p>Loading...</p>
            </div>
          )}
        </div>
      )}

      {/* Show promo code claim button if user doesn't have voucher credits and no forms are open */}
      {/* {!hasVoucherCredits && !showWithdrawForm && !showPromoForm && (
        <button 
          onClick={onPromoClick}
          className="promo-button"
        >
          🎫 Claim Free Credits
        </button>
      )} */}

      {/* Promo code form */}
      {showPromoForm && (
        <div className="promo-form">
          <div className="promo-form-header">
            <span>Claim Free Credits</span>
            <button onClick={onPromoCancel} className="promo-cancel">✕</button>
          </div>
          
          <div className="promo-form-field">
            <label>Promo Code:</label>
            <input
              type="text"
              value={promoCode}
              onChange={(e) => onPromoCodeChange(e.target.value.toUpperCase())}
              placeholder="Enter promo code..."
              className="promo-input"
            />
          </div>
          
          <div className="promo-form-actions">
            <button 
              onClick={onPromoSubmit}
              disabled={isClaimingPromo || !promoCode.trim()}
              className="promo-submit"
            >
              {isClaimingPromo ? 'Claiming...' : 'Claim Credits'}
            </button>
          </div>
        </div>
      )}

      {!showWithdrawForm && !showPromoForm && (
        <>
          {/* <button 
            onClick={onProfileClick}
            className="profile-button"
          >
            ⚡ Profile
          </button> */}
          

          
          <button 
            onClick={() => {
              onLogout();
              onClose();
            }}
            className="logout-button"
          >
            🚪 Logout
          </button>
        </>
      )}

              {/* Manual Refresh Option - always available */}
        <div className="dropdown-section">
          <button onClick={onManualRefresh} className="dropdown-button refresh-button">
            🔄 Refresh Wallet
          </button>
        </div>
    </div>

  </div>
);

// Main Component
const Wallet: React.FC<WalletProps> = ({ className, onLogout, onPastGamesClick }) => {
  const { isAnimating, transactionAmount } = useTransactionAnimation();
  const { clearSessionState } = useGameSession();
  // const { setShowProfileSetup } = useProfileSetup();
  
  // Use simplified wallet context
  const { 
    isAuthenticated,
    isReady,
    user,
    login,
    logout,
    address,
    balance,
    isLoading,
    solanaWallet, // Get the actual wallet object with signTransaction method
    getMaxWithdrawable,
    refreshBalance
  } = useWallet();
  
  // Voucher context
  const {
    hasVoucherCredits,
    totalVoucherCredits,
    claimPromoCode,
    refreshVoucherBalance
  } = useVoucherContext();
  
  // For now, set isGameMode to false (live mode only)
  const isGameMode = false;
  
  // Transaction hooks - use the actual wallet object, not a mock
  const { 
    withdrawSol
  } = useTransactions(solanaWallet);
  
  // Custom hooks
  const { isDropdownOpen, setIsDropdownOpen } = useDropdownControl();
  const { showCopyNotification, copyToClipboard } = useClipboard();
  
  // Withdraw state
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  
  // Promo code form state
  const [promoCode, setPromoCode] = useState('');
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [isClaimingPromo, setIsClaimingPromo] = useState(false);
  
  // Referral state
  const [showReferralSection, setShowReferralSection] = useState(false);
  const { myReferralCode, stats: referralStats, earnings: referralEarnings, loading: referralLoading, getMyCode, refreshStats, refreshEarnings, claimEarnings } = useReferral();
  const [showCopyReferralNotification, setShowCopyReferralNotification] = useState(false);
  
  


  // Clear component state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setIsDropdownOpen(false);
      setShowWithdrawForm(false);
      setWithdrawAddress('');
      setWithdrawAmount('');
      setPromoCode('');
      setShowPromoForm(false);
      setIsClaimingPromo(false);
      setIsWithdrawing(false);
      setShowReferralSection(false);
    }
  }, [isAuthenticated]);

  // NEW: Simple manual refresh function
  const handleManualRefresh = useCallback(async () => {
    console.log('🔄 Manual wallet refresh');
    
    try {
      // Try to refresh balance
      if (refreshBalance) {
        await refreshBalance();
      }
      
      // Also refresh voucher balance
      await refreshVoucherBalance();
      
      console.log('✅ Manual refresh completed');
    } catch (error) {
      console.error('❌ Manual refresh failed:', error);
    }
  }, [refreshBalance, refreshVoucherBalance]);

  // Watch for balance changes and trigger animations (simplified)
  // useBalanceAnimation(balance, [], isGameMode, showTransactionAnimation);

  const handleCopyAddress = () => {
    if (address) {
      copyToClipboard(address);
    }
  };

  const handleLogout = () => {
    // Clear local component state first
    setIsDropdownOpen(false);
    setShowWithdrawForm(false);
    setWithdrawAddress('');
    setWithdrawAmount('');
    setPromoCode('');
    setShowPromoForm(false);
    setIsClaimingPromo(false);
    setIsWithdrawing(false);
    setShowReferralSection(false);
    
    // Clear game session state
    clearSessionState();
    
    // Call custom logout handler (resets app state, etc.)
    if (onLogout) {
      onLogout();
    }
    // Then call Privy logout (handles OAuth logout)
    logout();
  };

  const handleWithdrawClick = () => {
    setShowWithdrawForm(true);
    setShowReferralSection(false); // Close other sections
  };

  const handleWithdrawCancel = () => {
    setShowWithdrawForm(false);
    setWithdrawAddress('');
    setWithdrawAmount('');
  };
  
  const handleReferralClick = async () => {
    if (!address) return;
    
    setShowReferralSection(true);
    setShowWithdrawForm(false); // Close other sections
    
    // Always fetch referral code if not loaded
    if (!myReferralCode) {
      await getMyCode();
    }
    
    // Always refresh stats and earnings to get latest data
    await Promise.all([refreshStats(), refreshEarnings()]);
  };
  
  const handleReferralCancel = () => {
    setShowReferralSection(false);
  };
  
  const handleCopyReferralLink = async () => {
    if (!myReferralCode) return;
    
    const referralUrl = `${window.location.origin}/${myReferralCode}`;
    await copyToClipboard(referralUrl);
    
    setShowCopyReferralNotification(true);
    setTimeout(() => setShowCopyReferralNotification(false), NOTIFICATION_DURATION);
  };
  
  const handleClaimEarnings = async () => {
    if (!address || referralLoading.claiming) return;
    
    const result = await claimEarnings();
    
    if (result.success) {
      // Refresh wallet balance after successful claim
      if (refreshBalance) {
        await refreshBalance();
      }
    }
    // UI will automatically update via context - no alerts needed
  };
  
  const handlePastGamesClick = () => {
    setIsDropdownOpen(false); // Close dropdown when opening modal
    if (onPastGamesClick) {
      onPastGamesClick();
    }
  };



  const handleProfileClick = () => {
    setIsDropdownOpen(false);
    // setShowProfileSetup(true);
  };

  // Promo code handlers
  const handlePromoClick = () => {
    setShowPromoForm(true);
  };

  const handlePromoCancel = () => {
    setShowPromoForm(false);
    setPromoCode('');
  };

  const handlePromoSubmit = async () => {
    if (!promoCode.trim() || isClaimingPromo) return;
    
    setIsClaimingPromo(true);
    try {
      const result = await claimPromoCode(promoCode.trim());
      
      if (result.success) {
        console.log('✅ Promo code claimed successfully:', result.data);
        
        // Clear form and close
        setPromoCode('');
        setShowPromoForm(false);
        
        // Refresh voucher balance
        await refreshVoucherBalance();
        
        // Show success (could add toast notification later)
        alert(`🎉 Success! You received ${result.data?.solCreditsAwarded} SOL in credits!`);
      } else {
        console.error('❌ Promo code claim failed:', result.error);
        alert(`❌ ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Promo code claim error:', error);
      alert('❌ Failed to claim promo code. Please try again.');
    } finally {
      setIsClaimingPromo(false);
    }
  };

  const handleWithdrawSubmit = async () => {
    if (!address || !balance || isWithdrawing) return;
    
    // Validate address
    const addressValidation = validateWalletAddress(withdrawAddress);
    if (!addressValidation.valid) {
      // Form validation - could add inline error display later
      return;
    }
    
    // Validate amount
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0 || amount > balance) {
      // Form validation - could add inline error display later
      return;
    }
    
    setIsWithdrawing(true);
    try {
      const result = await withdrawSol(withdrawAddress, amount);
      
      if (result.success) {
        console.log('✅ Withdrawal successful:', result.signature);
        
        // Wait a moment for confirmation, then refresh balance
        setTimeout(async () => {
          await refreshBalance();
        }, 2000);
        
        handleWithdrawCancel(); // Clear form on success
      } else {
        console.error('❌ Withdrawal failed:', result.error);
        // TODO: Show error to user with toast/modal
      }
    } catch (error: any) {
      console.error('❌ Withdrawal error:', error.message);
      // TODO: Show error to user with toast/modal
    } finally {
      setIsWithdrawing(false);
    }
  };

  // Render loading state
  if (!isReady) {
    return <LoadingState className={className} />;
  }

  return (
    <div className={`wallet authenticated ${className || ''}`}>
      {/* Show login button when not authenticated */}
      {!isAuthenticated ? (
        <LoginButton onLogin={login} />
      ) : (
        <div className="wallet-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <WalletSummary
            solBalance={balance}
            isLoadingBalance={isLoading}
          />
          <div className="dropdown-arrow">▼</div>
        </div>
      )}

      {/* Transaction animation overlay for both modes */}
      <TransactionAnimation
        isAnimating={isAnimating}
        transactionAmount={transactionAmount}
        isGameMode={isGameMode}
      />

      {/* Dropdown - show for both modes when authenticated */}
      {isAuthenticated && isDropdownOpen && (
        <WalletDropdown
          user={user}
          // isGameMode={isGameMode}
          // balance={balance}
          solBalance={balance}
          isLoadingBalance={isLoading}
          solanaWallet={solanaWallet}
          showCopyNotification={showCopyNotification}
          // gameInProgress={gameInProgress}
          showWithdrawForm={showWithdrawForm}
          withdrawAddress={withdrawAddress}
          withdrawAmount={withdrawAmount}
          isWithdrawing={isWithdrawing}
          promoCode={promoCode}
          
          // Voucher props
          hasVoucherCredits={hasVoucherCredits}
          totalVoucherCredits={totalVoucherCredits}
          showPromoForm={showPromoForm}
          isClaimingPromo={isClaimingPromo}
          
          // Referral props
          showReferralSection={showReferralSection}
          myReferralCode={myReferralCode}
          referralStats={referralStats}
          referralEarnings={referralEarnings}
          referralLoading={referralLoading}
          showCopyReferralNotification={showCopyReferralNotification}

          // onModeChange={handleModeChange}
          onCopyAddress={handleCopyAddress}
          onLogout={handleLogout}
          onWithdrawClick={handleWithdrawClick}
          onWithdrawCancel={handleWithdrawCancel}
          onWithdrawSubmit={handleWithdrawSubmit}
          onWithdrawAddressChange={setWithdrawAddress}
          onWithdrawAmountChange={setWithdrawAmount}

          onPromoCodeChange={setPromoCode}
          onPromoClick={handlePromoClick}
          onPromoCancel={handlePromoCancel}
          onPromoSubmit={handlePromoSubmit}
          onClose={() => setIsDropdownOpen(false)}
          // NEW: Simple manual refresh
          onManualRefresh={handleManualRefresh}
          getMaxWithdrawable={getMaxWithdrawable}
          onProfileClick={handleProfileClick}
          
          onReferralClick={handleReferralClick}
          onReferralCancel={handleReferralCancel}
          onCopyReferralLink={handleCopyReferralLink}
          onClaimEarnings={handleClaimEarnings}
          onPastGamesClick={handlePastGamesClick}
        />
      )}
    </div>
  );
};

export default Wallet; 